
import { AfterViewInit, Component, inject, input, output, signal } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { Canvas, Circle, FabricImage, FabricObject, Group, Point, TPointerEventInfo } from 'fabric';
import { AppCommonModule } from 'src/app/common.module';
import { ConfirmDialogComponent } from 'src/app/confirm-dialog/confirm-dialog.dialog';

export function getRect(item: { y: number; x: number }, selectable = false) {
  const rect = new Circle({
    top: item?.y ?? 100,
    left: item?.x ?? 100,
    width: 10,
    height: 10,
    radius: 10,
    fill: '#c5000f',
    backgroundColor: 'transparent',
    selectable,
  });
  return rect;
}

export function zoomToContents(canvas: Canvas) {
  //first check if there are any elemnts to zoom to
  if (canvas.getObjects().length < 1) {
    return;
  }
  // reset zoom so pan actions work as expected
  canvas.setZoom(1);
  //group all the objects
  const group = new Group(canvas.getObjects());
  //find the centre of the group on the canvas
  const x = (group.left + (group.width / 2)) - (canvas.width / 2);
  const y = (group.top + (group.height / 2)) - (canvas.height / 2);
  //and pan to it
  const absolutePanPoint = new Point(x, y);
  canvas.absolutePan(absolutePanPoint);
  //now we need to decide whether width or height should determine the scaling
  //e.g. a portrait box in a landscape canvas (height) needs scaling differently to a portrait box in a portrait canvas (could be height or width)
  //or a landscape box in a portrait canvas (width)
  //work out the distance between the edges of the group and the canvas
  const heightDist = canvas.getHeight() - group.height;
  const widthDist = canvas.getWidth() - group.width;
  let groupDimension = 0;
  let canvasDimension = 0;
  //the smaller the number then that's the side we need to use as a reference to scale
  //either group is inside the canvas (positive number) so the edge closest to the limits of the canvas will be used as the reference scale (smallest positive difference)
  //or the group extends outside the canvas so the edge that extends further will be the reference (large negative number)
  //either way, we want the smallest number
  if (heightDist < widthDist) {
    //height is the reference so need the height to scale to be nearly the height of the canvas
    groupDimension = group.height;
    canvasDimension = canvas.getHeight();
  } else {
    //width is the reference so need the width to scale to be nearly the width of the canvas
    groupDimension = group.width;
    canvasDimension = canvas.getWidth();
  }
  //work out how to scale the group to match the canvas size (then only make it zoom 80% of the way)
  const zoom = (canvasDimension / groupDimension) * 0.1;
  //we've already panned the canvas to the centre of the group, so now zomm using teh centre of teh canvas as teh reference point
  const zoomPoint = new Point(canvas.width / 2, canvas.height / 2);
  canvas.zoomToPoint(zoomPoint, zoom);
}

let lastTime: number;

@Component({
  selector: 'app-fabric',
  template: `
    <canvas [id]="id()"></canvas>
    `,
  imports: [
    AppCommonModule,
  ],
})
export class AppFabricComponent implements AfterViewInit {
  private modalCtrl = inject(ModalController);
  private translate = inject(TranslateService);

  private fcanvas: Canvas | null = null;
  private items: { x: number; y: number }[] = [];


  width = input(1200);
  height = input(1000);
  imgElement = input.required<HTMLImageElement>();
  points = input<{ url: string; x: number; y: number }[]>(null);
  disabled = input(false);

  save = output<{ x: number; y: number }[]>();
  cancelled = output();

  id = signal(new Date().getTime().toString());

  private add(item: { y: number; x: number }, selectable = false) {
    const o = getRect(item, selectable);
    if (selectable) {
      o.on('modified', (event) => {
        const x = event.target.left;
        const y = event.target.top;
        const existing = this.items.find((it) => it.x === item.x && it.y === item.y);
        existing.x = x;
        existing.y = y;
        this.save.emit(this.items);
      });
    }
    this.fcanvas?.add(o);
    return o;
  }

  private handleClick(event: TPointerEventInfo & {
    isClick: boolean;
    currentTarget?: FabricObject;
    currentSubTargets: FabricObject[];
  }) {
    if (event.currentTarget) {
      this.modalCtrl.create({
        component: ConfirmDialogComponent,
        componentProps: {
          title: this.translate.instant('general.delete') + '?',
          buttons: [this.translate.instant('general.close'), this.translate.instant('general.delete')],
        },
        cssClass: ['stack-modal'],
      }).then((m) => {
        m.present();

        m.onDidDismiss().then((data) => {
          if (data.data) {
            if (data.data.index === 0) {
              return;
            } else if (data.data.index === 1) {
              this.fcanvas.remove(event.currentTarget);
              this.items = this.items.filter((it) => it.x !== event.scenePoint.x && it.y !== event.scenePoint.y);
              this.save.emit(this.items);
            }
          }
        });
      });
    } else {
      const item = {
        x: event.scenePoint.x,
        y: event.scenePoint.y,
      };
      this.add(item, true);
      this.items.push(item);
      this.save.emit(this.items);
    }
  }

  private setupFabric() {
    this.fcanvas = new Canvas(this.id(), { allowTouchScrolling: true });
    if (this.points() != null) {
      this.points().map((it) => {
        this.add(it);
      });
      zoomToContents(this.fcanvas);
    }
    if (this.imgElement()) {
      const el = this.imgElement();
      const img = new FabricImage(el, {
        scaleX: 0.6,
        scaleY: 0.6,
      });
      // Get canvas dimensions
      const widthAspectRatio = this.width() / el.width;
      const heightAspectRatio = this.height() / el.height;
      const finalAspectRatio = Math.min(widthAspectRatio, heightAspectRatio);
      const height = el.height * finalAspectRatio;
      const width = el.width * finalAspectRatio;
      // Set dimension and image
      this.fcanvas.setDimensions({ width, height });
      this.fcanvas.backgroundImage = img;

      this.fcanvas.renderAll.bind(this.fcanvas);
      if (this.points() == null && !this.disabled()) {
        this.fcanvas.on('mouse:up', (event) => {
          if (event.isClick) {
            const date = new Date();
            const now = date.getTime();
            if (now - lastTime < 500) {
              this.handleClick(event);
            }
            lastTime = now;
          }
        });
      }
    }
  }

  ngAfterViewInit() {
    setTimeout(() => this.setupFabric(), 250);
  }
}
